/**
 * Pendientes: lo que hay que subir o hacer, planificado por cuándo toca.
 *
 * LA LISTA LA HACE LA APLICACIÓN, no el usuario. Sale de lo que ya sabe —los tickets que faltan
 * del mes, el extracto sin subir, la fianza sin depositar, el contrato sin PDF, el recibo
 * impagado, el seguro que caduca— y cada tarea desaparece sola cuando lo que la provoca se
 * resuelve. No hay que marcar nada como hecho: se hace, y se va.
 *
 * AGRUPADA POR PLAZO porque la pregunta con la que se entra es «¿qué tengo que hacer ya?»:
 * Vencido, Esta semana, Este mes, Más adelante. Las oportunidades (una plaza libre, una
 * valoración vieja) van aparte, al final y plegadas: son ideas, no deberes, y se pueden
 * descartar.
 *
 * CADA TAREA LLEVA A DONDE SE RESUELVE. El botón de la derecha abre la pantalla exacta —el mes
 * con el hueco, la ficha del inquilino—, para no tener que buscar después dónde se hacía.
 *
 * SUSTITUYE A LAS ALERTAS. Antes había una campana con su propia lista; ahora un impago es una
 * tarea más («Cobrar a…»), con su plazo, junto al resto del trabajo.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PendingBucket, PendingCategory, PendingTask } from '@naar/shared';
import { ApiError, api, queryKeys } from '../api/client';
import { useCan } from '../auth/AuthContext';
import { PageHeader } from '../components/PageHeader';
import { EmptyState, ErrorState } from '../components/EmptyState';
import { Skeleton, SkeletonPageHeader } from '../components/Skeleton';
import { Icon, type IconName } from '../components/Icon';
import { toast } from '../components/feedback/Toaster';
import { formatEurCents } from '../lib/format';
import styles from './PendingPage.module.css';

const GRUPOS: { id: Exclude<PendingBucket, 'oportunidad'>; titulo: string; ayuda: string }[] = [
  { id: 'vencido', titulo: 'Vencido', ayuda: 'Se pasó el plazo: lo primero.' },
  { id: 'semana', titulo: 'Esta semana', ayuda: 'Vence en los próximos 7 días.' },
  { id: 'mes', titulo: 'Este mes', ayuda: 'Sin prisa, pero antes de que acabe el mes.' },
  { id: 'despues', titulo: 'Más adelante', ayuda: 'Para tenerlo en el radar.' },
];

const ICONO: Record<PendingCategory, IconName> = {
  ticket: 'file',
  extracto: 'transfer',
  revision: 'eye',
  banco: 'transfer',
  conciliacion: 'check',
  cobro: 'cash-flow',
  contrato: 'file-lines',
  fianza: 'lock',
  documento: 'users',
  incidencia: 'wrench',
  seguro: 'building',
  fiscal: 'file-lines',
  oportunidad: 'star',
};

const ETIQUETA: Record<PendingCategory, string> = {
  ticket: 'Ticket',
  extracto: 'Extracto',
  revision: 'Revisar',
  banco: 'Banco',
  conciliacion: 'Conciliar',
  cobro: 'Cobro',
  contrato: 'Contrato',
  fianza: 'Fianza',
  documento: 'Documento',
  incidencia: 'Incidencia',
  seguro: 'Seguro',
  fiscal: 'Fiscal',
  oportunidad: 'Oportunidad',
};

type Filtro = 'todo' | 'subir' | 'hacer';

const DIA_MS = 86_400_000;

/** «Venció hace 3 días», «Vence hoy», «Vence mañana», «En 5 días», «12 nov». */
function plazo(iso: string | null): string | null {
  if (!iso) return null;
  const hoy = new Date();
  const hoyUtc = Date.UTC(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  const dia = new Date(`${iso}T00:00:00.000Z`).getTime();
  const dias = Math.round((dia - hoyUtc) / DIA_MS);
  if (dias < -1) return `Venció hace ${-dias} días`;
  if (dias === -1) return 'Venció ayer';
  if (dias === 0) return 'Vence hoy';
  if (dias === 1) return 'Vence mañana';
  if (dias <= 14) return `En ${dias} días`;
  return new Date(dia).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

export function PendingPage() {
  const queryClient = useQueryClient();
  const canManage = useCan('ledger:manage');
  const [filtro, setFiltro] = useState<Filtro>('todo');
  const [verOportunidades, setVerOportunidades] = useState(false);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: queryKeys.pending(),
    queryFn: () => api.pending(),
  });

  const descartar = useMutation({
    mutationFn: (task: PendingTask) => api.dismissPending(task.alertIds),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.pending() });
      toast.success('Descartado. No volverá a aparecer mientras siga igual.');
    },
    onError: (fault: unknown) =>
      toast.error(fault instanceof ApiError ? fault.message : 'No se ha podido descartar.'),
  });

  const visibles = useMemo(
    () => (data?.tasks ?? []).filter((task) => filtro === 'todo' || task.kind === filtro),
    [data, filtro],
  );
  const oportunidades = visibles.filter((task) => task.bucket === 'oportunidad');

  const header = (
    <PageHeader
      title="Pendientes"
      subtitle="Lo que hay que subir o hacer, ordenado por cuándo toca. Se actualiza solo: lo que se resuelve, desaparece."
    />
  );

  if (isPending) {
    return (
      <section>
        <SkeletonPageHeader />
        <Skeleton height={92} radius={8} />
        <div style={{ height: 16 }} />
        <Skeleton height={320} radius={8} />
      </section>
    );
  }

  if (error || !data) {
    return (
      <section>
        {header}
        <ErrorState error={error} onRetry={() => void refetch()} />
      </section>
    );
  }

  const { counts } = data;

  return (
    <section>
      {header}

      {/* --- Resumen: cuánto hay y para cuándo ------------------------------- */}
      <div className={styles.summary}>
        <div className={styles.summaryMain}>
          <span className={styles.summaryNumber}>{counts.total}</span>
          <span className={styles.summaryLabel}>
            {counts.total === 1 ? 'tarea pendiente' : 'tareas pendientes'}
            <span className={styles.summarySplit}>
              {counts.subir} para subir · {counts.hacer} para hacer
            </span>
          </span>
        </div>
        <div className={styles.buckets}>
          {GRUPOS.map((grupo) => (
            <a
              key={grupo.id}
              href={`#pend-${grupo.id}`}
              className={[
                styles.bucket,
                grupo.id === 'vencido' && counts.vencido > 0 ? styles.bucketDanger : '',
                counts[grupo.id] === 0 ? styles.bucketEmpty : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span className={styles.bucketCount}>{counts[grupo.id]}</span>
              <span className={styles.bucketLabel}>{grupo.titulo}</span>
            </a>
          ))}
        </div>
      </div>

      {/* --- Filtro: subir papeles o hacer cosas ----------------------------- */}
      <div className={styles.filters} role="group" aria-label="Qué mostrar">
        {(
          [
            ['todo', `Todo (${counts.total})`],
            ['subir', `Subir (${counts.subir})`],
            ['hacer', `Hacer (${counts.hacer})`],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={filtro === id ? `${styles.filter} ${styles.filterActive}` : styles.filter}
            aria-pressed={filtro === id}
            onClick={() => setFiltro(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {counts.total === 0 && (
        <EmptyState
          icon="check"
          title="No hay nada pendiente"
          body="Están todos los tickets, los cobros al día y los contratos en orden. Cuando falte algo, aparecerá aquí."
        />
      )}

      {/* --- Los grupos por plazo --------------------------------------------- */}
      {GRUPOS.map((grupo) => {
        const tareas = visibles.filter((task) => task.bucket === grupo.id);
        if (tareas.length === 0) return null;
        return (
          <section key={grupo.id} id={`pend-${grupo.id}`} className={styles.group}>
            <header className={styles.groupHead}>
              <h2
                className={
                  grupo.id === 'vencido' ? `${styles.groupTitle} ${styles.groupTitleDanger}` : styles.groupTitle
                }
              >
                {grupo.titulo}
                <span className={styles.groupCount}>{tareas.length}</span>
              </h2>
              <span className={styles.groupHint}>{grupo.ayuda}</span>
            </header>
            <ul className={styles.list}>
              {tareas.map((task) => (
                <TaskRow key={task.id} task={task} />
              ))}
            </ul>
          </section>
        );
      })}

      {/* --- Oportunidades, plegadas al final --------------------------------- */}
      {oportunidades.length > 0 && (
        <section className={styles.group}>
          <button
            type="button"
            className={styles.opportunitiesToggle}
            aria-expanded={verOportunidades}
            onClick={() => setVerOportunidades((value) => !value)}
          >
            <Icon name={verOportunidades ? 'chevron-down' : 'chevron-right'} size={15} strokeWidth={2.2} />
            <span className={styles.groupTitle}>
              Oportunidades
              <span className={styles.groupCount}>{oportunidades.length}</span>
            </span>
            <span className={styles.groupHint}>Plazas libres y valoraciones: ideas, no deberes.</span>
          </button>
          {verOportunidades && (
            <ul className={styles.list}>
              {oportunidades.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  {...(canManage && task.alertIds.length > 0
                    ? { onDismiss: () => descartar.mutate(task), dismissing: descartar.isPending }
                    : {})}
                />
              ))}
            </ul>
          )}
        </section>
      )}
    </section>
  );
}

function TaskRow({
  task,
  onDismiss,
  dismissing,
}: {
  task: PendingTask;
  onDismiss?: () => void;
  dismissing?: boolean;
}) {
  const cuando = plazo(task.dueDate);
  const vencida = task.bucket === 'vencido';
  const persona = task.tenant && !task.title.includes(task.tenant.fullName) ? task.tenant.fullName : null;
  const donde = [task.property?.name, persona].filter(Boolean).join(' · ');

  return (
    <li className={styles.row}>
      <span
        className={task.kind === 'subir' ? `${styles.icon} ${styles.iconUpload}` : styles.icon}
        aria-hidden="true"
      >
        <Icon name={task.kind === 'subir' ? 'upload' : ICONO[task.category]} size={16} strokeWidth={2} />
      </span>

      <div className={styles.main}>
        <div className={styles.titleRow}>
          <span className={styles.title}>{task.title}</span>
          <span className={styles.tag}>{ETIQUETA[task.category]}</span>
        </div>
        {task.detail && <p className={styles.detail}>{task.detail}</p>}
        {(donde || cuando) && (
          <div className={styles.meta}>
            {donde && <span>{donde}</span>}
            {cuando && <span className={vencida ? styles.dueLate : styles.due}>{cuando}</span>}
          </div>
        )}
      </div>

      <div className={styles.side}>
        {task.amount && <span className={styles.amount}>{formatEurCents(task.amount)}</span>}
        <div className={styles.actions}>
          {onDismiss && (
            <button type="button" className={styles.dismiss} onClick={onDismiss} disabled={dismissing}>
              Descartar
            </button>
          )}
          <Link to={task.link} className={styles.action}>
            {task.linkLabel}
            <Icon name="chevron-right" size={14} strokeWidth={2.2} />
          </Link>
        </div>
      </div>
    </li>
  );
}
